import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import api from '../lib/api';
import useAsync from '../hooks/useAsync';
import Loader, { ErrorState } from '../components/Loader';

const blank = { name: '', icon: '🎉', order: 0, active: true };

export default function AdminCategories() {
  const { data: items, loading, error, reload } = useAsync(
    () => api.get('/categories?all=true').then((r) => r.data),
    []
  );

  const [editing, setEditing] = useState(null); // category _id | 'new' | null
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm(blank); setEditing('new'); };
  const openEdit = (c) => {
    setForm({ name: c.name, icon: c.icon || '🎉', order: c.order || 0, active: c.active });
    setEditing(c._id);
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing === 'new') await api.post('/categories', payload);
      else await api.put(`/categories/${editing}`, payload);
      close();
      reload();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await api.delete(`/categories/${id}`);
    reload();
  };

  if (loading) return <Loader label="Loading categories…" />;
  if (error) return <ErrorState message="Couldn't load categories." onRetry={reload} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Categories</h1>
          <p className="text-sm text-ink-400">
            {items.length} occasions — these become the filter chips on “Our Packages” (website & app).
          </p>
        </div>
        <button onClick={openNew} className="btn-primary !py-2.5 text-sm">
          <Plus size={16} /> Add Category
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((c) => (
            <div key={c._id} className="glass flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/5 text-2xl">{c.icon}</span>
                <div>
                  <p className="font-medium text-white">{c.name}</p>
                  <p className="text-xs text-ink-400">
                    /{c.slug} · order {c.order || 0}
                    {!c.active && ' · hidden'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(c)} className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-ink-200 hover:bg-white/5">
                  <Pencil size={13} />
                </button>
                <button onClick={() => remove(c._id)} className="grid h-8 w-8 place-items-center rounded-lg border border-danger/30 text-danger hover:bg-danger/10">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        {items.length === 0 && (
          <p className="glass col-span-full py-12 text-center text-ink-400">No categories yet. Add your first occasion.</p>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur"
            onClick={close}
          >
            <motion.form
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={save}
              className="glass w-full max-w-md space-y-4 p-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold">{editing === 'new' ? 'Add Category' : 'Edit Category'}</h2>
                <button type="button" onClick={close}><X size={20} className="text-ink-400" /></button>
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-3">
                <label className="block">
                  <span className="mb-1 block text-sm text-ink-300">Name</span>
                  <input
                    type="text" value={form.name} required
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm text-ink-300">Icon</span>
                  <input
                    type="text" value={form.icon} maxLength={4}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    className="w-16 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-center text-xl text-white outline-none focus:border-brand"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1 block text-sm text-ink-300">Display order</span>
                <input
                  type="number" value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                />
              </label>

              <label className="flex items-center gap-2 text-sm text-ink-200">
                <input
                  type="checkbox" checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="h-4 w-4 accent-brand"
                />
                Active (visible to customers)
              </label>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={close} className="btn-ghost flex-1">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save'}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
